import React from 'react';
import {
  Box, Container, Grid, Typography, Paper, Stack, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LeaderboardRoundedIcon from '@mui/icons-material/LeaderboardRounded';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarIcon from '@mui/icons-material/Star';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import CampaignIcon from '@mui/icons-material/Campaign';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import VerifiedIcon from '@mui/icons-material/Verified';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import qrCode from '../homePage/images/qr-rankme.png';
import promoImage from './promo.png';
import staffAvatar from './profile.png';

const PHONE_NUMBER = '+94771141194';
const WEBSITE = 'www.rankme.lk';
const PURPLE = '#5B21B6';
const GOLD = '#FFD54F';

const POSITIVE_COMMENTS = [
  '"Alex\'s excellent service is why I\'ll return to this store."',
  '"Alex found exactly what I needed within minutes. Outstanding service!"',
  '"Chris\'s product knowledge helped me make the right choice."',
  '"Sam went the extra mile to help. Highly recommended!"',
  '"Alex was the most helpful assistant I\'ve met in a long time."',
];

const NEGATIVE_COMMENTS = [
  '"Chris was polite, but the service felt slow and unorganized."',
  '"Chris was busy chatting and kept me waiting."',
  '"Alex didn\'t follow up after promising to check availability."',
  '"Chris seemed distracted and made the process frustrating."',
];

const WHY_CHOOSE = [
  {
    icon: <SentimentVeryDissatisfiedIcon />,
    iconBg: '#6B21A8',
    title: 'Some sales assistants may provide poor customer service, but most dissatisfied customers never complain to management.',
    body: 'Instead, they quietly leave and share their negative experiences with friends, family, and on social media.',
  },
  {
    icon: <EmojiEventsIcon />,
    iconBg: '#1565C0',
    title: 'At the same time, it is difficult to identify and reward the assistants who consistently deliver outstanding service.',
    body: 'Many customers return to your store and recommend it to others because of these employees, yet their contributions often go unnoticed. Without recognition, valuable staff may eventually leave.',
  },
  {
    icon: <StorefrontIcon />,
    iconBg: '#1565C0',
    title: 'Managing customer experience across multiple branches is also challenging.',
    body: 'You cannot be present in every store to observe how employees interact with customers. Even traditional suggestion boxes are often ineffective, as customers are reluctant to write complaints in front of the same staff members.',
  },
  {
    icon: <WarningAmberIcon />,
    iconBg: '#E91E63',
    title: 'As a result, important customer feedback is lost, service issues remain unresolved, and top performers are not properly recognized.',
    body: '',
  },
  {
    icon: <StarIcon />,
    iconBg: '#F59E0B',
    title: 'This is where RankMe.lk helps.',
    body: 'Customers simply scan a QR code at your store and submit feedback about specific employees whenever it is convenient for them—even after they return home. This gives management direct insight into customer experiences, helps identify and reward top performers, and enables timely coaching for employees who need improvement.',
  },
];

const HOW_IT_WORKS = [
  { icon: <QrCode2Icon sx={{ fontSize: 32 }} />, label: 'Customer scans the QR code' },
  { icon: <StarIcon sx={{ fontSize: 32 }} />, label: 'Shares feedback in seconds' },
  { icon: <InsightsIcon sx={{ fontSize: 32 }} />, label: 'You receive real-time feedback & insights' },
  { icon: <TrendingUpIcon sx={{ fontSize: 32 }} />, label: 'Improve customer experience & grow' },
];

const VALUE_PROPS = [
  { icon: <StarIcon />, title: 'Happy customers become loyal customers' },
  { icon: <VerifiedIcon />, title: 'Resolve issues before they become bigger' },
  { icon: <InsightsIcon />, title: 'Real insights for better business decisions' },
  { icon: <EmojiEventsIcon />, title: 'Stronger reputation. Stronger business.' },
];

const CHART_POINTS: [number, number][] = [[10, 28], [36, 22], [62, 25], [88, 18], [114, 12], [140, 8]];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];

export default function PromoPage() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  return (
    <Box sx={{ bgcolor: '#fff', overflowX: 'hidden' }}>

      {/* ── Hero ── */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 4, md: 6 }, px: 2 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">

            {/* Left — brand + copy */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box sx={{ bgcolor: primary, borderRadius: 1.5, p: 0.75, display: 'flex' }}>
                  <LeaderboardRoundedIcon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: '0.08em', color: primary, textTransform: 'uppercase' }}>
                  RankMe
                </Typography>
              </Box>

              <Typography variant="subtitle2" color="text.secondary" sx={{ letterSpacing: 1, mb: 1.5, textTransform: 'uppercase' }}>
                Customer Feedback & Reputation Management Platform
              </Typography>

              <Typography variant="h3" fontWeight={800} sx={{ color: primary, lineHeight: 1.15, mb: 1.5 }}>
                Happy Customers.<br />Stronger Business.
              </Typography>

              <Typography variant="h6" fontWeight={700} sx={{ color: PURPLE, mb: 2 }}>
                Understand. Improve. Grow.
              </Typography>

              <Box sx={{ width: 48, height: 4, bgcolor: PURPLE, borderRadius: 2, mb: 2.5 }} />

              <Typography variant="body1" color="text.secondary" lineHeight={1.8}>
                RankMe helps you collect real customer feedback, improve customer
                satisfaction, and build a strong brand reputation.
              </Typography>
            </Grid>

            {/* Right — featured photo */}
            <Grid item xs={12} md={6}>
              {/* Image + desktop QR overlay */}
              <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={promoImage}
                  alt="Customer service in store"
                  sx={{
                    width: '100%',
                    maxWidth: 520,
                    height: { xs: 260, md: 380 },
                    objectFit: 'cover',
                    borderRadius: 4,
                    boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
                    display: 'block',
                  }}
                />

                {/* QR card overlay — desktop only */}
                <Paper elevation={6} sx={{
                  display: { xs: 'none', md: 'block' },
                  position: 'absolute', bottom: 16, right: 16,
                  p: 1.5, borderRadius: 2.5, textAlign: 'center',
                  width: 130, border: `2px solid ${PURPLE}`,
                  bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
                }}>
                  <Typography variant="caption" fontWeight={800} sx={{ color: PURPLE, fontSize: '0.6rem', display: 'block', mb: 0.75 }}>
                    WE VALUE YOUR FEEDBACK!
                  </Typography>
                  <Box component="img" src={qrCode} alt="QR Code" sx={{ width: 76, height: 76, display: 'block', mx: 'auto', mb: 0.75 }} />
                  <Typography variant="caption" fontWeight={700} sx={{ color: '#1a237e', fontSize: '0.55rem', display: 'block', mb: 1 }}>
                    SCAN & SHARE YOUR EXPERIENCE
                  </Typography>
                  <Box sx={{ bgcolor: PURPLE, borderRadius: 1.5, py: 0.5 }}>
                    <Typography variant="caption" fontWeight={800} sx={{ color: '#fff', fontSize: '0.65rem' }}>
                      Thank You!
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              {/* QR card — mobile only, shown below the image */}
              <Paper elevation={2} sx={{
                display: { xs: 'flex', md: 'none' },
                mt: 2, p: 2, borderRadius: 3,
                border: `2px solid ${PURPLE}`,
                alignItems: 'center', gap: 2,
              }}>
                <Box component="img" src={qrCode} alt="QR Code" sx={{ width: 80, height: 80, flexShrink: 0 }} />
                <Box>
                  <Typography variant="caption" fontWeight={800} sx={{ color: PURPLE, display: 'block', mb: 0.5 }}>
                    WE VALUE YOUR FEEDBACK!
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ color: '#1a237e', display: 'block', mb: 1 }}>
                    Scan & share your experience
                  </Typography>
                  <Box sx={{ bgcolor: PURPLE, borderRadius: 1.5, py: 0.4, px: 1.5, display: 'inline-block' }}>
                    <Typography variant="caption" fontWeight={800} sx={{ color: '#fff' }}>
                      Thank You!
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── How It Works ── */}
      <Box sx={{ bgcolor: '#f4f7fb', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Typography variant="subtitle1" fontWeight={800} textAlign="center" mb={3}
            sx={{ textTransform: 'uppercase', letterSpacing: 2, color: 'text.secondary' }}>
            How It Works
          </Typography>

          <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', bgcolor: '#fff', p: { xs: 2.5, md: 4 } }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={{ xs: 2, sm: 0 }}>
              {HOW_IT_WORKS.map((step, i) => (
                <React.Fragment key={step.label}>
                  <Box sx={{ textAlign: 'center', flex: '1 1 0', minWidth: { xs: '42%', sm: 0 } }}>
                    <Box sx={{ position: 'relative', display: 'inline-block', mb: 1.5 }}>
                      <Box sx={{
                        width: 64, height: 64, borderRadius: '50%',
                        background: `linear-gradient(135deg, ${primary} 0%, ${PURPLE} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                      }}>
                        {step.icon}
                      </Box>
                      <Box sx={{
                        position: 'absolute', top: -4, right: -4,
                        width: 22, height: 22, borderRadius: '50%',
                        bgcolor: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                      }}>
                        <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#1a1a2e' }}>{i + 1}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.5, maxWidth: 110, mx: 'auto' }}>
                      {step.label}
                    </Typography>
                  </Box>

                  {i < HOW_IT_WORKS.length - 1 && (
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', mt: '20px', px: 0.5 }}>
                      <ArrowForwardIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* ── Three-column section ── */}
      <Grid container>
        {/* Left — Why You Choose RankMe */}
        <Grid item xs={12} md={4} sx={{ bgcolor: '#fff', p: 0, overflow: 'hidden' }}>
          {/* Dark header bar */}
          <Box sx={{ bgcolor: primary, px: 3, py: 2 }}>
            <Typography fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1.5, color: '#fff', fontSize: '0.85rem' }}>
              Why You Choose RankMe
            </Typography>
          </Box>

          {/* Items */}
          <Stack divider={<Divider />} sx={{ px: { xs: 2, md: 2.5 } }}>
            {WHY_CHOOSE.map((item, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, py: 2, alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  bgcolor: item.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff',
                }}>
                  {item.icon}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#1a1a2e', lineHeight: 1.5, mb: item.body ? 0.75 : 0 }}>
                    {item.title}
                  </Typography>
                  {item.body && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6, display: 'block' }}>
                      {item.body}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </Grid>

        {/* Center — Phone mockup */}
        <Grid item xs={12} md={4} sx={{ bgcolor: '#f4f7fb', p: { xs: 3, md: 4 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{
            width: 196, borderRadius: '28px',
            border: '8px solid #1a1a2e',
            overflow: 'hidden', bgcolor: '#fff',
            boxShadow: '0 16px 48px rgba(0,0,0,0.28)',
          }}>
            {/* Notch */}
            <Box sx={{ bgcolor: '#1a1a2e', height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ width: 48, height: 7, bgcolor: '#2a2a3e', borderRadius: 4 }} />
            </Box>

            {/* App content */}
            <Box sx={{ p: 1.5 }}>
              {/* App header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.7rem', color: primary }}>RankMe</Typography>
                <Box sx={{ display: 'flex', gap: 0.4 }}>
                  {[0, 1, 2].map(i => <Box key={i} sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#bbb' }} />)}
                </Box>
              </Box>

              {/* Avatar */}
              <Box sx={{ textAlign: 'center', mb: 1 }}>
                <Box sx={{
                  width: 54, height: 54, borderRadius: '50%',
                  mx: 'auto', mb: 0.5, overflow: 'hidden',
                  border: '3px solid #dbeafe',
                }}>
                  <Box component="img" src={staffAvatar} alt="Staff" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700 }}>Alex</Typography>
                <Typography sx={{ fontSize: '0.5rem', color: 'text.secondary' }}>Sales Assistant</Typography>
              </Box>

              {/* Rating */}
              <Box sx={{ textAlign: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: '0.52rem', color: 'text.secondary' }}>Overall Rating</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: primary, lineHeight: 1.1 }}>4.6</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.2, my: 0.3 }}>
                  {[1, 2, 3, 4].map(i => <StarIcon key={i} sx={{ fontSize: 11, color: GOLD }} />)}
                  <StarIcon sx={{ fontSize: 11, color: '#e0e0e0' }} />
                </Box>
                <Typography sx={{ fontSize: '0.44rem', color: 'text.secondary' }}>(2,350 Feedbacks)</Typography>
              </Box>

              {/* Mini trend chart */}
              <Box sx={{ borderTop: '1px solid #eee', pt: 0.75 }}>
                <Typography sx={{ fontSize: '0.5rem', color: 'text.secondary', mb: 0.3 }}>Feedback Trend</Typography>
                <svg width="100%" height="38" viewBox="0 0 160 36">
                  <polyline
                    points={CHART_POINTS.map(([x, y]) => `${x},${y}`).join(' ')}
                    stroke={primary} strokeWidth="2" fill="none" strokeLinejoin="round"
                  />
                  {CHART_POINTS.map(([x, y], i) => (
                    <circle key={i} cx={x} cy={y} r="2.5" fill={primary} />
                  ))}
                </svg>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.2 }}>
                  {MONTHS.map(m => (
                    <Typography key={m} sx={{ fontSize: '0.38rem', color: 'text.disabled' }}>{m}</Typography>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Home bar */}
            <Box sx={{ bgcolor: '#1a1a2e', height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ width: 40, height: 4, bgcolor: '#444', borderRadius: 2 }} />
            </Box>
          </Box>
        </Grid>

        {/* Right — Top Comments */}
        <Grid item xs={12} md={4} sx={{ bgcolor: PURPLE, p: { xs: 3, md: 4 } }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#fff', mb: 2.5, fontSize: '0.75rem' }}>
            Top Comments
          </Typography>
          <Stack spacing={1.5}>
            {POSITIVE_COMMENTS.map((c, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <SentimentVerySatisfiedIcon sx={{ color: '#4CAF50', fontSize: 20, mt: 0.1, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>{c}</Typography>
              </Box>
            ))}
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 0.5 }} />
            {NEGATIVE_COMMENTS.map((c, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <SentimentVeryDissatisfiedIcon sx={{ color: '#FF6B6B', fontSize: 20, mt: 0.1, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>{c}</Typography>
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>

      {/* ── CTA Banner ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${primary} 0%, ${PURPLE} 100%)`,
        py: { xs: 5, md: 7 }, px: 2,
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Megaphone + stars */}
            <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
              <CampaignIcon sx={{ fontSize: 60, color: GOLD, mb: 0.5 }} />
              <Box sx={{ display: 'flex', gap: 0.4, justifyContent: 'center' }}>
                {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} sx={{ fontSize: 18, color: GOLD }} />)}
              </Box>
            </Grid>

            {/* Heading */}
            <Grid item xs={12} md={5}>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', mb: 0.5 }}>
                Your customers have a voice.
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                <Box component="span" sx={{ color: GOLD }}>RankMe </Box>
                <Box component="span" sx={{ color: '#fff' }}>helps you </Box>
                <Box component="span" sx={{ color: GOLD }}>listen, act and grow.</Box>
              </Typography>
            </Grid>

            {/* Value props grid */}
            <Grid item xs={12} md={5}>
              <Grid container spacing={2}>
                {VALUE_PROPS.map((v, i) => (
                  <Grid item xs={6} key={i}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{ color: GOLD, mb: 0.5 }}>{v.icon}</Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.5, display: 'block' }}>
                        {v.title}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ py: 3, px: 2, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ bgcolor: primary, borderRadius: 1.5, p: 0.75, display: 'flex' }}>
                <LeaderboardRoundedIcon sx={{ color: '#fff', fontSize: 22 }} />
              </Box>
              <Typography fontWeight={800} sx={{ letterSpacing: '0.08em', color: primary, textTransform: 'uppercase', fontSize: '1.05rem' }}>
                RankMe
              </Typography>
            </Box>

            <Box component="a" href={`tel:${PHONE_NUMBER}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none', color: 'inherit' }}>
              <PhoneIcon sx={{ color: primary, fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600}>{PHONE_NUMBER}</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon sx={{ color: primary, fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600}>{WEBSITE}</Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

    </Box>
  );
}
